import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Typography, Chip, Box, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Tooltip,
  Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon, Refresh as RefreshIcon,
  PlayArrow as StartIcon, Stop as StopIcon,
  Replay as ResendIcon, Delete as DeleteIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { AdminListPanel } from './AdminListPanel';
import { api } from '../../api/rpc';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import type { HL7Connection, HL7Message } from '../../api/types';

const connectionColumns: GridColDef[] = [
  {
    field: 'direction',
    headerName: 'Dir',
    width: 80,
    renderCell: (params) => (
      <Chip
        label={String(params.value ?? '—')}
        size="small"
        color={params.value === 'OUTBOUND' ? 'primary' : 'secondary'}
        sx={{ fontSize: 10, height: 18 }}
      />
    ),
  },
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'host', headerName: 'Host', width: 120 },
  { field: 'port', headerName: 'Port', width: 60 },
  { field: 'siteName', headerName: 'Site', width: 120 },
  {
    field: 'status',
    headerName: 'Status',
    width: 90,
    renderCell: (params) => {
      const s = String(params.value ?? '');
      const color = s === 'Started' ? 'success' : s === 'Stopped' ? 'error' : 'warning';
      return <Chip label={s || '—'} size="small" color={color} sx={{ fontSize: 10, height: 18 }} />;
    },
  },
  { field: 'totalMessages', headerName: 'Total', width: 60, type: 'number' },
  { field: 'pendingMessages', headerName: 'Active', width: 60, type: 'number' },
  {
    field: 'failedMessages',
    headerName: 'Failed',
    width: 60,
    type: 'number',
    renderCell: (params) => (
      <Typography
        variant="caption"
        sx={{ fontSize: 11, color: (params.value as number) > 0 ? 'error.main' : 'text.secondary' }}
      >
        {String(params.value ?? 0)}
      </Typography>
    ),
  },
  {
    field: 'actions',
    headerName: '',
    width: 100,
    sortable: false,
    renderCell: (params) => {
      const conn = params.row as HL7Connection;
      const isStarted = conn.status === 'Started';
      return (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title={isStarted ? 'Stop' : 'Start'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const state = isStarted ? 'stop' : 'start';
                api.setHL7QueueState(conn.id, conn.siteId ?? '', conn.direction === 'OUTBOUND', state)
                  .then(() => pushSuccess(`Connection ${state}ed`))
                  .catch((err) => pushError(`Failed: ${err instanceof Error ? err.message : String(err)}`));
              }}
              sx={{ width: 22, height: 22 }}
            >
              {isStarted ? <StopIcon sx={{ fontSize: 14 }} /> : <StartIcon sx={{ fontSize: 14, color: 'success.main' }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Pause">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                api.setHL7QueueState(conn.id, conn.siteId ?? '', conn.direction === 'OUTBOUND', 'pause_on')
                  .then(() => pushSuccess('Connection paused'))
                  .catch((err) => pushError(`Failed: ${err instanceof Error ? err.message : String(err)}`));
              }}
              sx={{ width: 22, height: 22 }}
            >
              <PauseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  },
];

const messageColumns: GridColDef[] = [
  { field: 'messageType', headerName: 'Type', width: 100 },
  { field: 'direction', headerName: 'Dir', width: 80 },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    renderCell: (params) => {
      const s = String(params.value ?? '');
      const color = s === 'Active' ? 'info' : s === 'Failed' ? 'error' : s === 'Success' ? 'success' : s === 'Cancel' ? 'default' : 'warning';
      return <Chip label={s || '—'} size="small" color={color} sx={{ fontSize: 10, height: 18 }} />;
    },
  },
  { field: 'hostPort', headerName: 'Host:Port', width: 140 },
  { field: 'patientId', headerName: 'Patient', width: 120 },
  { field: 'attempt', headerName: 'Attempts', width: 70, type: 'number' },
  {
    field: 'timestamp',
    headerName: 'Date',
    width: 160,
    valueFormatter: (value: number | null) =>
      value ? new Date(value).toLocaleString() : '—',
  },
  { field: 'errorMessage', headerName: 'Details', flex: 1, minWidth: 200 },
];

/**
 * HL7 Queue admin tab — HL7QueueListPanel equivalent.
 * Shows HL7 connections with message counts; click to drill into messages.
 */
export const HL7QueuePanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedConn, setSelectedConn] = useState<HL7Connection | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['hl7queue'],
    queryFn: async () => {
      try {
        const result = await api.getHL7Queue();
        return result.records ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 15_000,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['hl7queue'] });
  }, [queryClient]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(refresh, 15_000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refresh]);

  const connections = useMemo(() => {
    const all = data ?? [];
    if (!search) return all;
    const s = search.toLowerCase();
    return all.filter(
      (c) =>
        (c.name ?? '').toLowerCase().includes(s) ||
        (c.host ?? '').toLowerCase().includes(s) ||
        (c.siteName ?? '').toLowerCase().includes(s) ||
        (c.direction ?? '').toLowerCase().includes(s),
    );
  }, [data, search]);

  // Aggregate stats
  const stats = useMemo(() => {
    const all = data ?? [];
    const started = all.filter((c) => c.status === 'Started').length;
    const pending = all.reduce((a, c) => a + (c.pendingMessages ?? 0), 0);
    const failed = all.reduce((a, c) => a + (c.failedMessages ?? 0), 0);
    return { total: all.length, started, pending, failed };
  }, [data]);

  return (
    <>
      <AdminListPanel
        title="HL7 Queue"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        statusText={`${connections.length} connections · ${stats.started} active · ${stats.pending} pending${stats.failed ? ` · ${stats.failed} failed` : ''}`}
      >
        {/* Auto-refresh toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, borderBottom: '1px solid', borderColor: 'divider', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} sx={{ p: 0.25 }} />}
            label={<Typography sx={{ fontSize: 10 }}>Auto-refresh (15s)</Typography>}
            sx={{ ml: 0 }}
          />
        </Box>
        <DataGrid
          rows={connections}
          columns={connectionColumns}
          getRowId={(row) => (row as HL7Connection).id}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          rowHeight={24}
          columnHeaderHeight={28}
          onRowClick={(params) => setSelectedConn(params.row as HL7Connection)}
          sx={{
            border: 'none',
            fontSize: 12,
            cursor: 'pointer',
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </AdminListPanel>

      {selectedConn && (
        <HL7MessagesDialog
          connection={selectedConn}
          open
          onClose={() => setSelectedConn(null)}
        />
      )}
    </>
  );
};

/* ── HL7 Messages Dialog ── */
const HL7MessagesDialog = ({ connection, open, onClose }: {
  connection: HL7Connection; open: boolean; onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() });
  const isOutbound = connection.direction === 'OUTBOUND';

  const { data, isLoading } = useQuery({
    queryKey: ['hl7messages', connection.id],
    queryFn: async () => {
      try {
        const result = await api.getHL7QueueMessages(connection.id, 0, 500);
        return result;
      } catch {
        return { records: [] as HL7Message[], total: 0 };
      }
    },
    staleTime: 10_000,
  });

  const selectedIds = useMemo(
    () => (selected.type === 'include' ? [...selected.ids] as string[] : []),
    [selected],
  );
  const hasSelection = selectedIds.length > 0;

  const refreshMessages = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['hl7messages', connection.id] });
  }, [queryClient, connection.id]);

  const runAction = useCallback(async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      pushSuccess(`${label} completed`);
    } catch (e) {
      pushError(`${label} failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    refreshMessages();
  }, [refreshMessages]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh',
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        <Box>
          {connection.direction} — {connection.name} ({connection.host}:{connection.port})
          <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'text.secondary' }}>
            {data?.total ?? 0} messages{hasSelection ? ` · ${selectedIds.length} selected` : ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={data?.records ?? []}
          columns={messageColumns}
          getRowId={(row) => (row as HL7Message).id}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          checkboxSelection
          rowSelectionModel={selected}
          onRowSelectionModelChange={setSelected}
          rowHeight={24}
          columnHeaderHeight={28}
          sx={{
            border: 'none',
            fontSize: 12,
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Tooltip title={isOutbound ? 'Resend selected' : 'Reprocess selected'}>
          <span>
            <Button
              size="small"
              disabled={!hasSelection}
              startIcon={<ResendIcon sx={{ fontSize: 14 }} />}
              onClick={() => runAction(isOutbound ? 'Resend' : 'Reprocess', () => api.resendHL7Messages(connection.id, selectedIds, isOutbound))}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              {isOutbound ? 'Resend' : 'Reprocess'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Resend all failed">
          <Button
            size="small"
            startIcon={<ResendIcon sx={{ fontSize: 14 }} />}
            onClick={() => runAction(isOutbound ? 'Resend All Failed' : 'Reprocess All Failed', () => api.resendHL7Messages(connection.id, undefined, isOutbound))}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            {isOutbound ? 'Resend All Failed' : 'Reprocess All Failed'}
          </Button>
        </Tooltip>
        <Tooltip title="Delete (cancel) selected messages">
          <span>
            <Button
              size="small"
              disabled={!hasSelection}
              color="error"
              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
              onClick={() => runAction('Delete', () => api.deleteHL7Messages(connection.id, selectedIds))}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              Delete
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={refreshMessages}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          Refresh
        </Button>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
